"use client";

import { useEffect, useRef, useState } from "react";

export default function SuperadminLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const el: HTMLCanvasElement = canvas; // non-null alias for closures
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const context: CanvasRenderingContext2D = ctx; // non-null alias for closures

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W = 0;
    let H = 0;
    let raf = 0;
    let particles: Array<{ x: number; y: number; r: number; vy: number; vx: number; a: number; tw: number; ph: number }> = [];
    let streaks: Array<{ x: number; y: number; len: number; v: number; a: number }> = [];
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    function build() {
      particles = [];
      const n = 45;
      for (let i = 0; i < n; i++) {
        particles.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r: Math.random() * 1.7 + 0.5,
          vy: -(Math.random() * 0.26 + 0.06),
          vx: (Math.random() - 0.5) * 0.12,
          a: Math.random() * 0.45 + 0.2,
          tw: Math.random() * 0.022 + 0.005,
          ph: Math.random() * 6.28,
        });
      }
    }

    function resize() {
      W = el.width = window.innerWidth;
      H = el.height = window.innerHeight;
      build();
    }

    function frame() {
      context.clearRect(0, 0, W, H);
      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;
      const px = mouse.x / W - 0.5;
      const py = mouse.y / H - 0.5;

      for (const p of particles) {
        p.y += p.vy;
        p.x += p.vx;
        p.ph += p.tw;
        if (p.y < -10) {
          p.y = H + 10;
          p.x = Math.random() * W;
        }
        if (p.x < -10) p.x = W + 10;
        else if (p.x > W + 10) p.x = -10;
        const tw = 0.6 + Math.sin(p.ph) * 0.4;
        context.beginPath();
        context.arc(p.x - px * p.r * 20, p.y - py * p.r * 20, p.r, 0, 6.283);
        context.fillStyle = "rgba(222,186,108," + p.a * tw * 0.75 + ")";
        context.fill();
      }

      if (Math.random() < 0.13) {
        streaks.push({ x: Math.random() * W, y: -80, len: Math.random() * 150 + 100, v: Math.random() * 2.6 + 1.8, a: Math.random() * 0.2 + 0.14 });
      }
      for (let i = streaks.length - 1; i >= 0; i--) {
        const s = streaks[i];
        s.y += s.v;
        const g = context.createLinearGradient(s.x, s.y, s.x, s.y + s.len);
        g.addColorStop(0, "rgba(201,161,74,0)");
        g.addColorStop(1, "rgba(228,192,118," + s.a + ")");
        context.strokeStyle = g;
        context.lineWidth = 1.4;
        context.beginPath();
        context.moveTo(s.x, s.y);
        context.lineTo(s.x, s.y + s.len);
        context.stroke();
        if (s.y > H + s.len) streaks.splice(i, 1);
      }
      raf = requestAnimationFrame(frame);
    }

    function onMove(e: MouseEvent) {
      mouse.tx = e.clientX;
      mouse.ty = e.clientY;
    }

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    mouse.x = mouse.tx = W / 2;
    mouse.y = mouse.ty = H / 2;
    if (!reduce) {
      frame();
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await fetch("/api/superadmin/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j?.error || "Hibás felhasználónév vagy jelszó.");
        return;
      }
      window.location.href = "/admin";
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sl">
      <canvas ref={canvasRef} className="sl__canvas" />
      <div className="sl__aurora">
        <span className="a1" />
        <span className="a2" />
        <span className="a3" />
      </div>

      <div className="sl__left">
        <div className="sl__brand">
          B<em>e</em>ttornia<span className="glint" />
        </div>

        <div>
          <div className="sl__eyebrow">BETTORNIA · SUPERADMIN</div>
          <h1 className="sl__headline">
            Üdv újra.<em>Lássuk a számokat.</em>
          </h1>
          <p className="sl__lead">
            A Bettornia vezérlőterme — irodák, mátrix, felhasználók és elszámolások egy helyen. Lépj be a folytatáshoz.
          </p>
          <ul className="sl__list">
            <li>Kizárólag superadmin hozzáférés.</li>
            <li>A munkameneted 24 órán át él.</li>
            <li>Minden művelet naplózva.</li>
          </ul>
        </div>

        <div className="sl__foot">© 2026 One Click s.r.o. · Bettornia Superadmin</div>
      </div>

      <div className="sl__right">
        <form className="sl__form" onSubmit={submit}>
          <h2 className="sl__title">Belépés</h2>
          <p className="sl__sub">Felhasználónév és jelszó szükséges.</p>

          {err ? <div className="sl__err">{err}</div> : null}

          <div className="sl__field">
            <label className="sl__label" htmlFor="u">
              Felhasználónév
            </label>
            <input
              id="u"
              className="sl__input"
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="sl__field">
            <label className="sl__label" htmlFor="p">
              Jelszó
            </label>
            <input
              id="p"
              className="sl__input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button className="sl__btn" type="submit" disabled={loading}>
            {loading ? "Belépés…" : "Belépés"}
          </button>

          <div className="sl__hint">Biztonságos munkamenet · superadmin.bettornia.com</div>
        </form>
      </div>
    </div>
  );
}
